<template>
    <div class="max-w-2xl w-full text-center">
        <div class="bg-white border rounded-2xl p-7.5 mb-6">
            <h1 class="display-serif text-valence-grey-850 mb-1.5">Perspective</h1>
            <p class="body-small text-valence-grey-750 mb-2 md:mb-5">
                {{
                    collaborationProfileEnabled
                        ? "Helps build your collaboration profile so colleagues know how to work best with you"
                        : "Helps you understand your working style so you can collaborate better"
                }}
            </p>

            <hr class="hidden md:block border-gray-200 mb-8" />

            <div class="flex flex-col md:flex-row md:justify-center md:items-start divide-y md:divide-y-0 md:divide-x divide-gray-200 mb-4 md:mb-10 text-left md:text-center">
                <div
                    v-for="card in cards"
                    :key="card.label"
                    class="flex flex-row md:flex-col items-center gap-4 md:gap-3 py-4 md:py-0 md:px-8 w-full max-w-xs md:max-w-none mx-auto md:mx-0 md:w-52">
                    <div class="size-14 md:h-11 md:w-26 shrink-0 flex items-center justify-center">
                        <component :is="card.icon" class="size-full" />
                    </div>
                    <div class="flex flex-col min-w-0 md:contents">
                        <span class="body-small text-valence-grey-600">{{ card.step }}</span>
                        <span class="nadia-chat text-valence-grey-850">{{ card.label }}</span>
                    </div>
                </div>
            </div>
        </div>

        <BaseButton v-if="isCheckingAssessmentStatus" theme="primary" disabled> Checking Assessment Status... </BaseButton>
        <BaseButton v-else-if="hasInProgressSession" theme="primary" @click="emit('continue')"> Continue Assessment </BaseButton>
        <BaseButton v-else theme="primary" :disabled="isStarting" @click="emit('start')">
            <span v-if="isStarting">Starting...</span>
            <span v-else>Take the Quiz</span>
        </BaseButton>

        <p v-if="errorMessage" role="alert" class="body-small text-amber-600 mt-3">{{ errorMessage }}</p>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import PerspectiveCircles from "~vue/components/PerspectiveCircles.vue";
import PerspectiveStyleCircles from "~vue/components/PerspectiveStyleCircles.vue";
import PerspectiveQuizIcon from "~vue/icons/PerspectiveQuizIcon.vue";

interface Props {
    hasInProgressSession: boolean;
    isCheckingAssessmentStatus?: boolean;
    isStarting: boolean;
    errorMessage: string | null;
    collaborationProfileEnabled?: boolean;
}

const { isCheckingAssessmentStatus = false, hasInProgressSession = false, isStarting = false, errorMessage = null, collaborationProfileEnabled = false } = defineProps<Props>();

const emit = defineEmits<{
    start: [];
    continue: [];
}>();

const cards = [
    { icon: PerspectiveQuizIcon, step: "Step 1", label: "Take a Quick Quiz" },
    { icon: PerspectiveCircles, step: "Step 2", label: "Uncover your Working Style" },
    { icon: PerspectiveStyleCircles, step: "Step 3", label: "Get Personalized Coaching" },
];
</script>
